import { Trans, useTranslation } from 'react-i18next';
import { TYPE } from '../language';

interface ContextBannerProps {
  isAggregate: boolean;
  /** `undefined` until the org's loop list has resolved this loop's name. */
  navLoopName: string | undefined;
  /** The loop actually on screen — meaningless while `isAggregate`, `undefined` until resolved. */
  focusedLoopName: string | undefined;
  /** The findings in scope — before the objective filter, so the banner doesn't shift when someone
   * narrows the reading. */
  findingsCount: number;
  /** Meaningless unless `isAggregate`. */
  readableLoopCount: number;
  onReset: () => void;
  onSwitchToThisLoop: () => void;
}

/** Bolds the counted facts — findings, loops, the switched-to loop's name — so they read apart from
 * the surrounding secondary prose rather than blending into it. */
const BOLD = { bold: <span className="font-medium text-text-neutral-primary" /> };

/**
 * States which loop's run is on screen, whenever that is not the loop the reader navigated to.
 *
 * Once a reader can move off the loop they arrived on, the page has to say so — reading another
 * loop's findings while believing they are the nav loop's is worse than not offering the switch at
 * all (the ticket's own framing). Rendered only when `scope !== navLoopId`; the nav-scope reading
 * discloses nothing here because there is nothing to disclose.
 *
 * Waits for both names it needs rather than rendering a partial sentence that then jitters as
 * `useAllLoopsQuery` resolves — nothing here beats showing nothing briefly.
 *
 * The findings and loop counts each get their own pluralized phrase (`findingsPhrase`,
 * `loopsPhrase`) composed into the sentence via `<Trans>`'s `values`, rather than one template
 * carrying two independent `_one`/`_other` splits — i18next's plural suffix is driven by a single
 * `count`, so two counts in one sentence need two separately resolved phrases.
 */
export const ContextBanner = ({
  isAggregate,
  navLoopName,
  focusedLoopName,
  findingsCount,
  readableLoopCount,
  onReset,
  onSwitchToThisLoop,
}: ContextBannerProps) => {
  const { t } = useTranslation();

  if (navLoopName === undefined) return null;
  if (!isAggregate && focusedLoopName === undefined) return null;

  const findingsPhrase = t(($) => $.loopAgenticInsights.experimental.loopSwitcher.findingsPhrase, {
    count: findingsCount,
  });

  return (
    <div
      data-testid="agentic-x-context-banner"
      // Purple in both modes — do not reintroduce a neutral rail for All loops.
      className="mt-4 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 border-l-2 border-border-brand py-0.5 pl-3"
    >
      <span className={`${TYPE.body} max-w-[72ch]`}>
        {isAggregate ? (
          <Trans
            i18nKey="loopAgenticInsights.experimental.loopSwitcher.aggregateContext"
            values={{
              findings: findingsPhrase,
              loops: t(($) => $.loopAgenticInsights.experimental.loopSwitcher.loopsPhrase, {
                count: readableLoopCount,
              }),
            }}
            components={BOLD}
          />
        ) : (
          <Trans
            i18nKey="loopAgenticInsights.experimental.loopSwitcher.switchedContext"
            values={{ findings: findingsPhrase, loopName: focusedLoopName }}
            components={BOLD}
          />
        )}
      </span>
      <span className="flex items-center gap-3.5">
        <button
          type="button"
          data-testid="agentic-x-context-reset"
          onClick={onReset}
          className="text-xs text-text-neutral-secondary underline decoration-border-neutral-bold decoration-1 underline-offset-4 hover:text-text-neutral-primary"
        >
          {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.backToLoop, {
            loop: navLoopName,
          })}
        </button>
        {isAggregate ? null : (
          <>
            <span className="text-xs text-text-neutral-tertiary" aria-hidden="true">
              ·
            </span>
            <button
              type="button"
              data-testid="agentic-x-context-switch"
              onClick={onSwitchToThisLoop}
              className="text-xs text-text-brand-primary underline decoration-border-brand decoration-1 underline-offset-4 hover:text-text-brand-primary-hovered"
            >
              {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.switchToLoop, {
                loop: focusedLoopName,
              })}
            </button>
          </>
        )}
      </span>
    </div>
  );
};
